import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Table from '../../components/UI/Table';
import Modal from '../../components/UI/Modal';
import { trainingAPI, certificateSettingsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Search,
  AlertCircle,
  Eye,
  CheckCircle2,
  Download,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileDown,
  CheckSquare,
  Square,
  ArrowUpDown,
} from 'lucide-react';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  background: ${props => props.theme.colors.white};
  min-width: 200px;
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.background.secondary};
  color: ${props => props.theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const TextArea = styled.textarea`
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: ${props => props.theme.typography.fontSize.xs};
  text-transform: uppercase;
  color: ${props => props.theme.colors.text.secondary};
  letter-spacing: 0.05em;
`;

const DetailValue = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  word-break: break-word;
`;

const RegistrationTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
  padding: 0.75rem;
    border-bottom: 1px solid ${props => props.theme.colors.border.light};
    text-align: left;
  }

  th {
  font-size: ${props => props.theme.typography.fontSize.sm};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${props => props.theme.colors.text.secondary};
    position: sticky;
    top: 0;
    background: ${props => props.theme.colors.background.primary};
    z-index: 1;
  }

  td {
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  }

  tbody tr:hover {
    background-color: ${props => props.theme.colors.background.secondary};
  }
`;

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background-color: ${props => props.theme.colors.background.secondary};
  }
  
  svg {
    margin-left: 0.25rem;
    opacity: ${props => props.active ? 1 : 0.3};
  }
`;

const RegistrationFilters = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding: 1rem;
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem;
  border-top: 1px solid ${props => props.theme.colors.border.light};
`;

const PaginationInfo = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const BulkActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: ${props => props.theme.colors.primary}15;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
`;

const CheckboxCell = styled.td`
  width: 40px;
  padding: 0.5rem !important;
  
  input[type="checkbox"] {
    cursor: pointer;
    width: 18px;
    height: 18px;
  }
`;

const StatusDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: capitalize;
  color: ${props => {
    switch (props.status) {
      case 'approved':
      case 'completed':
      case 'attended':
        return props.theme.colors.success;
      case 'declined':
        return props.theme.colors.error;
      case 'waitlisted':
        return props.theme.colors.warning;
      default:
        return props.theme.colors.info;
    }
  }};
`;

const FormDetailsWrapper = styled.div`
  margin-top: 0.75rem;
  padding: 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border.light};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FormDetailsTitle = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FormDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem 1.25rem;
`;

const PreviewTitle = styled.h3`
  margin: 0.5rem 0 0.75rem;
  text-align: center;
  font-size: 2rem;
  letter-spacing: 0.5em;
  color: #182334;
`;

const PreviewLine = styled.div`
  width: ${props => props.width || '220px'};
  height: 3px;
  margin: 0 auto;
  background: #c8a046;
`;

const PreviewStatement = styled.p`
  text-transform: uppercase;
  letter-spacing: 0.35em;
  text-align: center;
  font-size: 0.75rem;
  margin: 2rem 0 0.5rem;
  color: #7a7d85;
`;

const PreviewRecipient = styled.div`
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const PreviewBody = styled.p`
  text-align: center;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #3f3f46;
  max-width: 640px;
  margin: 1rem auto 0;
`;

const PreviewQuote = styled.p`
  margin-top: 1.5rem;
  text-align: center;
  font-style: italic;
  color: #5f6470;
  font-size: 0.9rem;
  letter-spacing: 0.05em;
`;

const PreviewMeta = styled.div`
  text-align: center;
  font-size: 0.85rem;
  color: #4b5563;
  margin-top: 1.25rem;
`;

const PreviewFooter = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  font-size: 0.8rem;
  color: #6b7280;
`;

const PreviewSignatureLine = styled.div`
  width: 220px;
  height: 1px;
  background: #c8a046;
  margin-bottom: 0.4rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FormFieldLabel = styled.span`
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const FormFieldValue = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  word-break: break-word;
  white-space: pre-wrap;
`;

const CertificatePreviewCard = styled(Card)`
  padding: 1.5rem;
  margin-top: 1.5rem;
`;

const CertificatePreviewFrame = styled.div`
  width: 100%;
  background: #f8f7f1;
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid rgba(24, 35, 52, 0.08);
  padding: 20px;
  display: flex;
  justify-content: center;
`;

const CertificateCanvas = styled.div`
  width: min(520px, 100%);
  aspect-ratio: 210 / 297;
  background: #dfd7c5;
  padding: 12px;
  border: 1px solid rgba(26, 37, 55, 0.15);
`;

const CertificateFrameBorder = styled.div`
  width: 100%;
  height: 100%;
  border: 8px solid #cba74a;
  padding: 14px;
  position: relative;
  background: #f9f6ed;

  &::before {
    content: '';
    position: absolute;
    top: 14px;
    bottom: 14px;
    left: 14px;
    right: 14px;
    border: 2px solid #cba74a;
    pointer-events: none;
  }
`;

const CertificateContent = styled.div`
  position: relative;
  height: 100%;
  padding: 32px 28px 26px;
  font-family: inherit;
  color: #1a2537;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h1 {
    letter-spacing: 0.32em;
    font-size: 28px;
    margin-bottom: 6px;
  }

  h2 {
    margin: 14px 0 6px;
    font-size: 24px;
    letter-spacing: 0.22em;
  }

  p {
    margin: 0.35rem auto;
    max-width: 420px;
    color: #4b5563;
    line-height: 1.45;
    font-size: 13px;
  }
`;

const prettifyFieldLabel = (label = '') => {
  if (!label) return 'Response';
  return label
    .toString()
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const TrainingManagement = () => {
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', type: '' });
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    training_type: 'workshop',
    start_date: '',
    end_date: '',
    location: '',
    trainer_name: '',
    trainer_organization: '',
    max_participants: '',
    requires_approval: true,
    max_registrations_per_person: '',
    is_active: true,
  });
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [registrationSummary, setRegistrationSummary] = useState(null);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
  const [registrationFormSchema, setRegistrationFormSchema] = useState(null);
  const [registrationDetail, setRegistrationDetail] = useState(null);
  const [showRegistrationDetailModal, setShowRegistrationDetailModal] = useState(false);
  const [certificateDownloadId, setCertificateDownloadId] = useState(null);
  const [certificateReissueId, setCertificateReissueId] = useState(null);
  const [certificateSettings, setCertificateSettings] = useState(null);
  const [certificateViewId, setCertificateViewId] = useState(null);
  
  // Registration filtering and pagination
  const [registrationSearch, setRegistrationSearch] = useState('');
  const [registrationStatusFilter, setRegistrationStatusFilter] = useState('');
  const [registrationSortBy, setRegistrationSortBy] = useState('created_at');
  const [registrationSortOrder, setRegistrationSortOrder] = useState('desc');
  const [registrationPage, setRegistrationPage] = useState(1);
  const [registrationPageSize] = useState(25);
  const [selectedRegistrations, setSelectedRegistrations] = useState(new Set());

  const fetchTrainings = async () => {
    setLoading(true);
    try {
      const params = {
        per_page: 100,
        search: filters.search || undefined,
        training_type: filters.type || undefined,
      };
      const response = await trainingAPI.getAll(params);
      const payload = response.data?.data || response.data;
      setTrainings(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error('Failed to load trainings:', error);
      toast.error('Unable to load training programs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.type]);

  useEffect(() => {
    const fetchCertificateSettings = async () => {
      try {
        const { data } = await certificateSettingsAPI.get();
        setCertificateSettings(data);
      } catch (error) {
        console.error('Failed to load certificate settings:', error);
      }
    };
    fetchCertificateSettings();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'create') {
      openForm(null);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const openForm = (training = null) => {
    setEditingTraining(training);
    if (training) {
      setFormData({
        title: training.title || '',
        description: training.description || '',
        training_type: training.training_type || 'workshop',
        start_date: training.start_date || '',
        end_date: training.end_date || '',
        location: training.location || '',
        trainer_name: training.trainer_name || '',
        trainer_organization: training.trainer_organization || '',
        max_participants: training.max_participants || '',
        requires_approval:
          typeof training.requires_approval === 'boolean' ? training.requires_approval : true,
        max_registrations_per_person: training.max_registrations_per_person || '',
        is_active: training.is_active ?? true,
      });
    } else {
      setFormData({
        title: '',
        description: '',
        training_type: 'workshop',
        start_date: '',
        end_date: '',
        location: '',
        trainer_name: '',
        trainer_organization: '',
        max_participants: '',
        requires_approval: true,
        max_registrations_per_person: '',
        is_active: true,
      });
    }
    setShowFormModal(true);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      title: formData.title,
      description: formData.description,
      training_type: formData.training_type,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      location: formData.location,
      trainer_name: formData.trainer_name,
      trainer_organization: formData.trainer_organization,
      max_participants: formData.max_participants
        ? Number(formData.max_participants)
        : null,
      requires_approval: formData.requires_approval,
      max_registrations_per_person: formData.max_registrations_per_person
        ? Number(formData.max_registrations_per_person)
        : null,
      is_active: formData.is_active,
    };

    try {
    if (editingTraining) {
        await trainingAPI.update(editingTraining.id, payload);
        toast.success('Training updated.');
    } else {
        await trainingAPI.create(payload);
        toast.success('Training created.');
      }
      setShowFormModal(false);
      fetchTrainings();
    } catch (error) {
      console.error('Failed to save training:', error);
      const message = error.response?.data?.message || 'Unable to save training.';
      toast.error(message);
    }
  };

  const handleSyncTrainings = async () => {
    if (!window.confirm('Sync all completed trainings to member profiles? This will link training history based on email addresses.')) {
      return;
    }

    setIsSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/training/sync-completed`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          `Sync completed! Created: ${data.summary.new_records_created}, Updated: ${data.summary.existing_records_updated}, Skipped: ${data.summary.skipped_no_member}`
        );
      } else {
        toast.error(data.message || 'Failed to sync trainings');
      }
    } catch (error) {
      console.error('Failed to sync trainings:', error);
      toast.error('Failed to sync trainings');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDelete = async (training) => {
    if (!window.confirm(`Delete "${training.title}"? This action cannot be undone.`)) return;
    try {
      await trainingAPI.delete(training.id);
      toast.success('Training deleted.');
      fetchTrainings();
    } catch (error) {
      console.error('Failed to delete training:', error);
      toast.error('Unable to delete training.');
    }
  };

  const openRegistrationsModal = async (training) => {
    // Navigate to the dedicated registrations page
    navigate(`/app/training/${training.id}/registrations`);
  };

  const handleRegistrationStatus = async (registration, status) => {
    let rejectionReason;
    if (status === 'declined') {
      rejectionReason = window.prompt('Optional: provide a reason for declining', '');
    }
    if (status === 'completed') {
      const confirmComplete = window.confirm('Mark this participant as completed and issue their certificate?');
      if (!confirmComplete) return;
    }
    try {
      await trainingAPI.updateRegistrationStatus(registration.id, {
        status,
        rejection_reason: rejectionReason,
      });
      toast.success(`Registration ${status}.`);
      if (selectedTraining) {
        openRegistrationsModal(selectedTraining);
      }
      if (registrationDetail?.id === registration.id) {
        setRegistrationDetail((prev) =>
          prev
            ? {
                ...prev,
                status,
                rejection_reason: rejectionReason,
              }
            : prev
        );
      }
    } catch (error) {
      console.error('Failed to update registration:', error);
      const message = error.response?.data?.message || 'Unable to update registration.';
      toast.error(message);
    }
  };

  const handleDownloadCertificate = async (registration) => {
    setCertificateDownloadId(registration.id);
    try {
      const response = await trainingAPI.downloadCertificate(registration.id);
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const trainingTitle = selectedTraining?.title || registration.trainingProgram?.title || 'training';
      link.href = url;
      link.download = `${registration.full_name || 'participant'} - ${trainingTitle} certificate.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
    } catch (error) {
      console.error('Failed to download certificate:', error);
      const message = error.response?.data?.message || 'Unable to download certificate.';
      toast.error(message);
    } finally {
      setCertificateDownloadId(null);
    }
  };

  const handleRegenerateCertificate = async (registration) => {
    const confirmed = window.confirm('Reissue this certificate and resend it to the participant?');
    if (!confirmed) return;
    setCertificateReissueId(registration.id);
    try {
      const response = await trainingAPI.regenerateCertificate(registration.id);
      toast.success(response.data?.message || 'Certificate reissued and emailed.');
      if (registrationDetail?.id === registration.id) {
        setRegistrationDetail((prev) =>
          prev
            ? {
                ...prev,
                certificate: response.data?.data || prev.certificate,
              }
            : prev
        );
      }
      if (selectedTraining) {
        openRegistrationsModal(selectedTraining);
      }
    } catch (error) {
      console.error('Failed to regenerate certificate:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
      
      let message = 'Unable to regenerate certificate.';
      if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.response?.status === 422) {
        message = 'Registration must be completed before generating a certificate.';
      } else if (error.response?.status === 404) {
        message = 'Registration not found.';
      } else if (error.response?.status === 500) {
        message = 'Server error occurred. Please check server logs or try again later.';
      } else if (error.message) {
        message = error.message;
      }
      
      toast.error(message);
    } finally {
      setCertificateReissueId(null);
    }
  };

  const handleViewCertificate = async (registration) => {
    setCertificateViewId(registration.id);
    try {
      const response = await trainingAPI.downloadCertificate(registration.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (error) {
      console.error('Failed to preview certificate:', error);
      const message = error.response?.data?.message || 'Unable to preview certificate.';
      toast.error(message);
    } finally {
      setCertificateViewId(null);
    }
  };

  const openRegistrationDetail = (registration) => {
    setRegistrationDetail(registration);
    setShowRegistrationDetailModal(true);
  };

  const closeRegistrationDetail = () => {
    setShowRegistrationDetailModal(false);
    setRegistrationDetail(null);
  };

  const getFieldLabel = (fieldKey) => {
    if (!fieldKey) return '';
    const normalizedKey = String(fieldKey);
    const fields = registrationFormSchema?.fields || [];
    const match = fields.find(
      (field) =>
        String(field.key ?? field.id ?? field.name ?? '') === normalizedKey
    );
    return match?.label || normalizedKey;
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return Object.entries(value)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
    }
    return value.toString();
  };

  const columns = useMemo(() => [
    {
      header: 'Title',
      accessor: 'title',
      width: '260px',
    },
    {
      header: 'Type',
      accessor: 'training_type',
      render: (value) => value?.replace('_', ' ') || '—',
    },
    {
      header: 'Dates',
      accessor: 'start_date',
      render: (value, row) => {
        if (!value) return 'TBD';
        const end = row.end_date ? ` → ${new Date(row.end_date).toLocaleDateString()}` : '';
        return `${new Date(value).toLocaleDateString()}${end}`;
      },
    },
    {
      header: 'Location',
      accessor: 'location',
    },
    {
      header: 'Capacity',
      accessor: 'max_participants',
      render: (value, row) => {
        if (!value) return '—';
        const reserved = getReservedSeats(row);
        const remaining = getRemainingSeats(row);
        const isFull = row.is_full ?? (remaining !== null ? remaining <= 0 : false);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
            <span>{reserved}/{value} reserved</span>
            <span style={{ fontSize: '0.85rem', color: isFull ? theme.colors.error : theme.colors.text.secondary }}>
              {isFull ? 'Registration full' : `${remaining ?? '—'} seats left`}
            </span>
          </div>
        );
      },
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button 
            size="small"
            variant="ghost"
            leftIcon={<Users size={14} />}
            onClick={() => openRegistrationsModal(row)}
          >
            Registrations
          </Button>
          {hasPermission('edit_training') && (
            <Button 
              size="small"
              variant="ghost"
              leftIcon={<Edit size={14} />}
              onClick={() => openForm(row)}
            >
              Edit
            </Button>
          )}
          {hasPermission('delete_training') && (
            <Button 
              size="small"
              variant="ghost"
              leftIcon={<Trash2 size={14} />}
              onClick={() => handleDelete(row)}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ], [hasPermission, theme]);

  const getReservedSeats = (training) => {
    if (!training) return 0;
    if (typeof training.reserved_seats === 'number') {
      return training.reserved_seats;
    }
    const approved = training.approved_registrations_count || 0;
    const pending = training.pending_registrations_count || 0;
    const waitlisted = training.waitlisted_registrations_count || 0;
    return approved + pending + waitlisted;
  };

  const getRemainingSeats = (training) => {
    if (!training || !training.max_participants) return null;
    if (typeof training.remaining_seats === 'number') {
      return training.remaining_seats;
    }
    return Math.max(training.max_participants - getReservedSeats(training), 0);
  };

  const buildRegistrationResponses = (registration) => {
    const responses = [];
    if (registration.membership_id) {
      responses.push({
        label: 'Membership ID',
        value: registration.membership_id,
      });
    }
    if (registration.form_data && typeof registration.form_data === 'object') {
      Object.entries(registration.form_data).forEach(([key, value]) => {
        responses.push({
          label: getFieldLabel(key) || prettifyFieldLabel(key),
          value: formatFieldValue(value),
        });
      });
    }

    [
      { key: 'email', label: 'Email' },
      { key: 'motivation', label: 'Motivation for Attending' },
      { key: 'special_needs', label: 'Special Requirements' },
      { key: 'region', label: 'Region' },
      { key: 'phone', label: 'Phone Number' },
      { key: 'organization', label: 'Organization' },
      { key: 'role_title', label: 'Role / Title' },
    ].forEach(({ key, label }) => {
      if (registration[key]) {
        responses.push({
          label,
          value: formatFieldValue(registration[key]),
        });
      }
    });

    return responses.filter((response) => response.value && response.value !== '—');
  };

  const registrationDetailResponses = useMemo(() => {
    if (!registrationDetail) return [];
    return buildRegistrationResponses(registrationDetail);
  }, [registrationDetail]);

  const filteredTrainings = useMemo(() => {
    if (!filters.search) return trainings;
    return trainings.filter(training =>
      training.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      training.description?.toLowerCase().includes(filters.search.toLowerCase())
    );
  }, [trainings, filters.search]);

  // Filtered and sorted registrations
  const filteredRegistrations = useMemo(() => {
    let filtered = [...registrations];

    // Search filter
    if (registrationSearch) {
      const searchLower = registrationSearch.toLowerCase();
      filtered = filtered.filter(reg =>
        reg.full_name?.toLowerCase().includes(searchLower) ||
        reg.email?.toLowerCase().includes(searchLower) ||
        reg.organization?.toLowerCase().includes(searchLower) ||
        reg.phone?.toLowerCase().includes(searchLower) ||
        reg.membership_id?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (registrationStatusFilter) {
      filtered = filtered.filter(reg => reg.status === registrationStatusFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal = a[registrationSortBy];
      let bVal = b[registrationSortBy];

      if (registrationSortBy === 'created_at' || registrationSortBy === 'approved_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (registrationSortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return filtered;
  }, [registrations, registrationSearch, registrationStatusFilter, registrationSortBy, registrationSortOrder]);

  // Paginated registrations
  const paginatedRegistrations = useMemo(() => {
    const start = (registrationPage - 1) * registrationPageSize;
    const end = start + registrationPageSize;
    return filteredRegistrations.slice(start, end);
  }, [filteredRegistrations, registrationPage, registrationPageSize]);

  const totalPages = Math.ceil(filteredRegistrations.length / registrationPageSize);

  const handleSort = (column) => {
    if (registrationSortBy === column) {
      setRegistrationSortOrder(registrationSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setRegistrationSortBy(column);
      setRegistrationSortOrder('desc');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRegistrations(new Set(paginatedRegistrations.map(r => r.id)));
    } else {
      setSelectedRegistrations(new Set());
    }
  };

  const handleSelectRegistration = (id, checked) => {
    const newSet = new Set(selectedRegistrations);
    if (checked) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedRegistrations(newSet);
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedRegistrations.size === 0) {
      toast.error('Please select at least one registration.');
      return;
    }

    const confirmMessage = `Update ${selectedRegistrations.size} registration(s) to "${status}"?`;
    if (!window.confirm(confirmMessage)) return;

    const updates = Array.from(selectedRegistrations).map(id => {
      const reg = registrations.find(r => r.id === id);
      return trainingAPI.updateRegistrationStatus(id, { status });
    });

    try {
      await Promise.all(updates);
      toast.success(`${selectedRegistrations.size} registration(s) updated.`);
      setSelectedRegistrations(new Set());
      if (selectedTraining) {
        openRegistrationsModal(selectedTraining);
      }
    } catch (error) {
      console.error('Failed to update registrations:', error);
      toast.error('Some registrations could not be updated.');
    }
  };

  const exportRegistrations = () => {
    const headers = ['Name', 'Email', 'Phone', 'Organization', 'Status', 'Registered On'];
    const rows = filteredRegistrations.map(reg => [
      reg.full_name || '',
      reg.email || '',
      reg.phone || '',
      reg.organization || '',
      reg.status || '',
      reg.created_at ? new Date(reg.created_at).toLocaleString() : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `${selectedTraining?.title || 'training'}_registrations_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Registrations exported successfully.');
  };

  return (
    <PageContainer>
      <Header>
        <div>
          <Title theme={theme}>Manage Training Programs</Title>
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>
            Publish trainings, review registrations, and keep participation fair.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {hasPermission('add_training') && (
            <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => openForm()}>
              Add Training
            </Button>
          )}
        </div>
      </Header>

      {hasPermission('manage_training') && (
        <>
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Training Completion Emails</h3>
              <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                When you mark a participant as "Completed", they automatically receive a congratulatory email with training details and next steps.
              </p>
              <span style={{ fontSize: '0.9rem', color: theme.colors.text.secondary }}>
                Customize the email content, greeting, and signature to match your organization's tone.
              </span>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <Button variant="outline" size="small" onClick={() => navigate('/app/training/email-templates')}>
                  Customize Email Template
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Sync Training History</h3>
              <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                Link all completed training registrations to member profiles. This will automatically add training history to members who registered using their email address.
              </p>
              <span style={{ fontSize: '0.9rem', color: theme.colors.text.secondary }}>
                Run this if you want to retroactively sync past completed trainings to member profiles.
              </span>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <Button variant="primary" size="small" onClick={handleSyncTrainings} disabled={isSyncing}>
                  {isSyncing ? 'Syncing...' : 'Sync Completed Trainings'}
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}

      <Card>
        <FiltersRow>
          <Input
            placeholder="Search trainings..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            leftIcon={<Search size={14} />}
          />
          <Select value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}>
            <option value="">All Types</option>
            <option value="workshop">Workshop</option>
            <option value="seminar">Seminar</option>
            <option value="conference">Conference</option>
            <option value="online_course">Online Course</option>
            <option value="field_training">Field Training</option>
          </Select>
        </FiltersRow>
      </Card>

      <Table
        columns={columns}
        data={filteredTrainings}
        loading={loading}
        emptyMessage="No training programs found"
      />
      
      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={editingTraining ? 'Edit Training' : 'Add Training'}
        size="large"
      >
        <form onSubmit={handleFormSubmit}>
          <FormGrid>
            <FieldGroup>
              <label>Title</label>
                <Input
                required
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
              />
            </FieldGroup>
            <FieldGroup>
              <label>Training Type</label>
                <Select
                name="training_type"
                value={formData.training_type}
                  onChange={handleInputChange}
              >
                <option value="workshop">Workshop</option>
                <option value="seminar">Seminar</option>
                <option value="conference">Conference</option>
                <option value="online_course">Online Course</option>
                <option value="field_training">Field Training</option>
                </Select>
            </FieldGroup>
            <FieldGroup>
              <label>Start Date</label>
                <Input
                  type="date"
                name="start_date"
                value={formData.start_date}
                  onChange={handleInputChange}
              />
            </FieldGroup>
            <FieldGroup>
              <label>End Date</label>
                <Input
                  type="date"
                name="end_date"
                value={formData.end_date}
                  onChange={handleInputChange}
              />
            </FieldGroup>
            <FieldGroup>
              <label>Location</label>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
              />
            </FieldGroup>
            <FieldGroup>
              <label>Trainer Name</label>
                <Input
                name="trainer_name"
                value={formData.trainer_name}
                  onChange={handleInputChange}
              />
            </FieldGroup>
            <FieldGroup>
              <label>Max Participants</label>
              <Input
                type="number"
                name="max_participants"
                value={formData.max_participants}
                onChange={handleInputChange}
              />
            </FieldGroup>
            <FieldGroup>
              <label>Require Approval</label>
              <Select
                name="requires_approval"
                value={formData.requires_approval ? 'yes' : 'no'}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, requires_approval: e.target.value === 'yes' }))
                }
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Select>
            </FieldGroup>
            <FieldGroup>
              <label>Max Registrations / Person</label>
                <Input
                type="number"
                name="max_registrations_per_person"
                value={formData.max_registrations_per_person}
                  onChange={handleInputChange}
              />
            </FieldGroup>
          </FormGrid>
          <FieldGroup style={{ marginTop: '1rem' }}>
            <label>Description</label>
              <TextArea
              name="description"
              value={formData.description}
                  onChange={handleInputChange}
            />
          </FieldGroup>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <Button type="button" variant="ghost" onClick={() => setShowFormModal(false)} style={{ marginRight: '0.75rem' }}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
              {editingTraining ? 'Update Training' : 'Create Training'}
              </Button>
            </div>
        </form>
      </Modal>

      <Modal
        isOpen={showRegistrationsModal}
        onClose={() => setShowRegistrationsModal(false)}
        title={selectedTraining ? `Registrations • ${selectedTraining.title}` : 'Training Registrations'}
        size="large"
      >
        {registrationLoading ? (
          <p>Loading registrations…</p>
        ) : registrations.length === 0 ? (
          <Card padding="2rem" textAlign="center">
            No registrations yet.
          </Card>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <Badge>Total: {registrationSummary?.total ?? registrations.length}</Badge>
              <Badge>Pending: {registrationSummary?.pending ?? 0}</Badge>
              <Badge>Approved: {registrationSummary?.approved ?? 0}</Badge>
              <Badge>Declined: {registrationSummary?.declined ?? 0}</Badge>
              <Badge>Waitlisted: {registrationSummary?.waitlisted ?? 0}</Badge>
              {selectedTraining?.max_participants && (
                <Badge
                  style={{
                    background: (selectedTraining?.is_full ? theme.colors.error : theme.colors.primary) + '15',
                    color: selectedTraining?.is_full ? theme.colors.error : theme.colors.primary,
                  }}
                >
                  {selectedTraining?.is_full
                    ? `Capacity reached (${selectedTraining.max_participants})`
                    : `${getRemainingSeats(selectedTraining)} of ${selectedTraining.max_participants} seats left`}
                </Badge>
              )}
            </div>
            {selectedTraining?.is_full && (
              <p style={{ color: theme.colors.error, marginTop: 0, marginBottom: '1rem' }}>
                This training has reached capacity. New registrations are blocked automatically.
              </p>
            )}

            <RegistrationFilters theme={theme}>
              <Input
                placeholder="Search by name, email, organization..."
                value={registrationSearch}
                onChange={(e) => {
                  setRegistrationSearch(e.target.value);
                  setRegistrationPage(1);
                }}
                leftIcon={<Search size={14} />}
                style={{ flex: 1, minWidth: '250px' }}
              />
              <Select
                value={registrationStatusFilter}
                onChange={(e) => {
                  setRegistrationStatusFilter(e.target.value);
                  setRegistrationPage(1);
                }}
                style={{ minWidth: '150px' }}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="waitlisted">Waitlisted</option>
                <option value="declined">Declined</option>
                <option value="attended">Attended</option>
                <option value="completed">Completed</option>
              </Select>
              <Button
                variant="outline"
                size="small"
                leftIcon={<FileDown size={14} />}
                onClick={exportRegistrations}
              >
                Export CSV
              </Button>
            </RegistrationFilters>

            {selectedRegistrations.size > 0 && (
              <BulkActionsBar theme={theme}>
                <span style={{ fontWeight: 600 }}>
                  {selectedRegistrations.size} selected
                </span>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="primary"
                    onClick={() => handleBulkStatusUpdate('approved')}
                  >
                    Approve Selected
                  </Button>
                  <Button
                    size="small"
                    variant="outline"
                    onClick={() => handleBulkStatusUpdate('waitlisted')}
                  >
                    Waitlist Selected
                  </Button>
                  <Button
                    size="small"
                    variant="danger"
                    onClick={() => handleBulkStatusUpdate('declined')}
                  >
                    Decline Selected
                  </Button>
                  <Button
                    size="small"
                    variant="ghost"
                    onClick={() => setSelectedRegistrations(new Set())}
                  >
                    Clear Selection
                  </Button>
                </div>
              </BulkActionsBar>
            )}

            <div style={{ maxHeight: '60vh', overflowY: 'auto', border: `1px solid ${theme.colors.border.light}`, borderRadius: theme.borderRadius.md }}>
              <RegistrationTable theme={theme}>
                <thead>
                  <tr>
                    <CheckboxCell as="th" style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={paginatedRegistrations.length > 0 && paginatedRegistrations.every(r => selectedRegistrations.has(r.id))}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </CheckboxCell>
                    <SortableHeader
                      theme={theme}
                      active={registrationSortBy === 'full_name'}
                      onClick={() => handleSort('full_name')}
                    >
                      Applicant
                      <ArrowUpDown size={12} />
                    </SortableHeader>
                    <SortableHeader
                      theme={theme}
                      active={registrationSortBy === 'organization'}
                      onClick={() => handleSort('organization')}
                    >
                      Organization
                      <ArrowUpDown size={12} />
                    </SortableHeader>
                    <SortableHeader
                      theme={theme}
                      active={registrationSortBy === 'status'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                      <ArrowUpDown size={12} />
                    </SortableHeader>
                    <SortableHeader
                      theme={theme}
                      active={registrationSortBy === 'created_at'}
                      onClick={() => handleSort('created_at')}
                    >
                      Registered
                      <ArrowUpDown size={12} />
                    </SortableHeader>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRegistrations.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
                        {registrationSearch || registrationStatusFilter
                          ? 'No registrations match your filters.'
                          : 'No registrations found.'}
                      </td>
                    </tr>
                  ) : (
                    paginatedRegistrations.map((reg) => (
                      <tr key={reg.id}>
                        <CheckboxCell theme={theme}>
                          <input
                            type="checkbox"
                            checked={selectedRegistrations.has(reg.id)}
                            onChange={(e) => handleSelectRegistration(reg.id, e.target.checked)}
                          />
                        </CheckboxCell>
                        <td>
                          <div style={{ fontWeight: 600 }}>{reg.full_name}</div>
                          <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                            {reg.email}
                          </div>
                          {reg.membership_id && (
                            <div style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                              Member ID: {reg.membership_id}
                            </div>
                          )}
                        </td>
                        <td>
                          {reg.organization || '—'}
                          {reg.role_title && (
                            <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                              {reg.role_title}
                            </div>
                          )}
                        </td>
                        <td>
                          <StatusDot status={reg.status}>{reg.status}</StatusDot>
                          {reg.participation_history > 0 && (
                            <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginTop: '0.25rem' }}>
                              {reg.participation_history} previous
                            </div>
                          )}
                        </td>
                        <td>
                          {reg.created_at
                            ? new Date(reg.created_at).toLocaleDateString()
                            : '—'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="ghost"
                              leftIcon={<Eye size={14} />}
                              onClick={() => openRegistrationDetail(reg)}
                            >
                              View
                            </Button>
                            {reg.status !== 'approved' && reg.status !== 'completed' && (
                              <Button
                                size="small"
                                variant="primary"
                                onClick={() => handleRegistrationStatus(reg, 'approved')}
                              >
                                Approve
                              </Button>
                            )}
                            {reg.status !== 'waitlisted' && (
                              <Button
                                size="small"
                                variant="outline"
                                onClick={() => handleRegistrationStatus(reg, 'waitlisted')}
                              >
                                Waitlist
                              </Button>
                            )}
                            {reg.status !== 'declined' && (
                              <Button
                                size="small"
                                variant="danger"
                                onClick={() => handleRegistrationStatus(reg, 'declined')}
                              >
                                Decline
                              </Button>
                            )}
                            {reg.status === 'approved' && (
                              <Button
                                size="small"
                                variant="secondary"
                                leftIcon={<CheckCircle2 size={14} />}
                                onClick={() => handleRegistrationStatus(reg, 'completed')}
                              >
                                Complete
                              </Button>
                            )}
                            {reg.status === 'completed' && (
                              <>
                                <Button
                                  size="small"
                                  variant="ghost"
                                  leftIcon={<Download size={14} />}
                                  loading={certificateDownloadId === reg.id}
                                  onClick={() => handleDownloadCertificate(reg)}
                                  title="Download Certificate"
                                />
                                <Button
                                  size="small"
                                  variant="ghost"
                                  leftIcon={<Eye size={14} />}
                                  loading={certificateViewId === reg.id}
                                  onClick={() => handleViewCertificate(reg)}
                                  title="View Certificate"
                                />
                                <Button
                                  size="small"
                                  variant="ghost"
                                  leftIcon={<RefreshCw size={14} />}
                                  loading={certificateReissueId === reg.id}
                                  onClick={() => handleRegenerateCertificate(reg)}
                                  title="Reissue Certificate"
                                />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </RegistrationTable>
            </div>

            {totalPages > 1 && (
              <PaginationControls theme={theme}>
                <PaginationInfo theme={theme}>
                  Showing {((registrationPage - 1) * registrationPageSize) + 1} to {Math.min(registrationPage * registrationPageSize, filteredRegistrations.length)} of {filteredRegistrations.length} registrations
                </PaginationInfo>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="ghost"
                    leftIcon={<ChevronLeft size={14} />}
                    onClick={() => setRegistrationPage(p => Math.max(1, p - 1))}
                    disabled={registrationPage === 1}
                  >
                    Previous
                  </Button>
                  <span style={{ fontSize: theme.typography.fontSize.sm }}>
                    Page {registrationPage} of {totalPages}
                  </span>
                  <Button
                    size="small"
                    variant="ghost"
                    rightIcon={<ChevronRight size={14} />}
                    onClick={() => setRegistrationPage(p => Math.min(totalPages, p + 1))}
                    disabled={registrationPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </PaginationControls>
            )}
          </>
        )}
      </Modal>

      <Modal
        isOpen={showRegistrationDetailModal}
        onClose={closeRegistrationDetail}
        title={registrationDetail ? `Application • ${registrationDetail.full_name}` : 'Application Detail'}
        size="large"
      >
        {registrationDetail ? (
          <>
            <FormDetailsWrapper theme={theme} style={{ marginTop: 0 }}>
              <FormDetailsGrid>
                <FormField>
                  <FormFieldLabel theme={theme}>Applicant</FormFieldLabel>
                  <FormFieldValue theme={theme}>{registrationDetail.full_name}</FormFieldValue>
                </FormField>
                <FormField>
                  <FormFieldLabel theme={theme}>Email</FormFieldLabel>
                  <FormFieldValue theme={theme}>{registrationDetail.email}</FormFieldValue>
                </FormField>
                <FormField>
                  <FormFieldLabel theme={theme}>Phone</FormFieldLabel>
                  <FormFieldValue theme={theme}>{registrationDetail.phone || '—'}</FormFieldValue>
                </FormField>
                <FormField>
                  <FormFieldLabel theme={theme}>Status</FormFieldLabel>
                  <FormFieldValue theme={theme}>{registrationDetail.status}</FormFieldValue>
                </FormField>
                {registrationDetail.membership_id && (
                  <FormField>
                    <FormFieldLabel theme={theme}>Membership ID</FormFieldLabel>
                    <FormFieldValue theme={theme}>{registrationDetail.membership_id}</FormFieldValue>
                  </FormField>
                )}
                {registrationDetail.status === 'completed' && (
                  <FormField>
                    <FormFieldLabel theme={theme}>Certificate</FormFieldLabel>
                    {registrationDetail.certificate ? (
                      <FormFieldValue
                        theme={theme}
                        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                      >
                        <span>Certificate No: {registrationDetail.certificate.certificate_number}</span>
                        <span>
                          Issued:{' '}
                          {registrationDetail.certificate.issued_at
                            ? new Date(registrationDetail.certificate.issued_at).toLocaleDateString()
                            : '—'}
                        </span>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="primary"
                            leftIcon={<Download size={14} />}
                            loading={certificateDownloadId === registrationDetail.id}
                            onClick={() => handleDownloadCertificate(registrationDetail)}
                          >
                            Download PDF
                          </Button>
                          <Button
                            size="small"
                            variant="outline"
                            leftIcon={<Eye size={14} />}
                            loading={certificateViewId === registrationDetail.id}
                            onClick={() => handleViewCertificate(registrationDetail)}
                          >
                            View Certificate
                          </Button>
                          <Button
                            size="small"
                            variant="ghost"
                            leftIcon={<RefreshCw size={14} />}
                            loading={certificateReissueId === registrationDetail.id}
                            onClick={() => handleRegenerateCertificate(registrationDetail)}
                          >
                            Reissue
                          </Button>
                        </div>
                      </FormFieldValue>
                    ) : (
                      <FormFieldValue theme={theme}>
                        Certificate is being prepared.
                        <Button
                          variant="ghost"
                          size="small"
                          style={{ marginLeft: '0.5rem' }}
                          loading={certificateReissueId === registrationDetail.id}
                          onClick={() => handleRegenerateCertificate(registrationDetail)}
                        >
                          Generate now
                        </Button>
                      </FormFieldValue>
                    )}
                  </FormField>
                )}
                {registrationDetail.member && (
                  <FormField>
                    <FormFieldLabel theme={theme}>Linked Member</FormFieldLabel>
                    <FormFieldValue theme={theme}>
                      {registrationDetail.member.full_name || registrationDetail.member.member_id}
                      <Button
                        variant="ghost"
                        size="small"
                        style={{ marginTop: '0.25rem', paddingLeft: 0 }}
                        onClick={() => navigate(`/app/members/${registrationDetail.member.id}`)}
                      >
                        Open Profile
                      </Button>
                    </FormFieldValue>
                  </FormField>
                )}
                <FormField>
                  <FormFieldLabel theme={theme}>Submitted On</FormFieldLabel>
                  <FormFieldValue theme={theme}>
                    {registrationDetail.created_at
                      ? new Date(registrationDetail.created_at).toLocaleString()
                      : '—'}
                  </FormFieldValue>
                </FormField>
              </FormDetailsGrid>
            </FormDetailsWrapper>

            {registrationDetail.status === 'completed' && (
              <CertificatePreviewCard>
                <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Certificate Preview</h3>
                <CertificatePreview
                  registration={registrationDetail}
                  training={selectedTraining}
                  settings={certificateSettings}
                />
                <p style={{ marginTop: '0.75rem', color: theme.colors.text.secondary, fontSize: '0.9rem' }}>
                  Review what the certificate looks like before downloading or reissuing it for this participant.
                </p>
              </CertificatePreviewCard>
            )}

            <FormDetailsWrapper theme={theme}>
              <FormDetailsTitle theme={theme}>Form Responses</FormDetailsTitle>
              {registrationDetailResponses.length === 0 ? (
                <FormFieldValue theme={theme}>No additional responses were provided.</FormFieldValue>
              ) : (
                <FormDetailsGrid>
                  {registrationDetailResponses.map((response, index) => (
                    <FormField key={`detail-${index}`}>
                      <FormFieldLabel theme={theme}>{response.label}</FormFieldLabel>
                      <FormFieldValue theme={theme}>{response.value}</FormFieldValue>
                    </FormField>
                  ))}
                </FormDetailsGrid>
              )}
            </FormDetailsWrapper>
          </>
        ) : (
          <p>No registration selected.</p>
        )}
      </Modal>
    </PageContainer>
  );
};

export default TrainingManagement;

const CertificatePreview = ({ registration, training, settings }) => {
  const participantName = registration?.full_name || registration?.name || 'Participant';
  const trainingTitle = training?.title || 'EWHRDN Training Program';
  const dateSummary = buildTrainingDateSummary(training);
  const location = training?.location || '';
  const issuedDate = registration?.certificate?.issued_at
    ? new Date(registration.certificate.issued_at).toLocaleDateString(undefined, {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      })
    : new Date().toLocaleDateString(undefined, {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      });
  const certificateNumber = registration?.certificate?.certificate_number || 'TC-000000';
  const verificationCode = registration?.certificate?.verification_code || 'XXXXXXXXXXXX';

  const orientation = settings?.orientation || 'portrait';
  const aspectRatio = orientation === 'landscape' ? '297 / 210' : '210 / 297';
  const logoSrc = settings?.logo_path ? `/storage/${settings.logo_path}` : null;
  const signatureSrc = settings?.signature_path ? `/storage/${settings.signature_path}` : null;

  const tokens = {
    '{{participant_name}}': participantName,
    '{{training_title}}': trainingTitle,
    '{{training_dates}}': dateSummary || '',
    '{{training_location}}': location,
    '{{issued_date}}': issuedDate,
    '{{organization}}': 'EWHRDN',
  };

  const replaceTokens = (text = '') =>
    Object.keys(tokens).reduce((acc, key) => acc.replaceAll(key, tokens[key]), text || '');

  const titleText = settings?.title_text || 'CERTIFICATE';
  const introText = replaceTokens(settings?.intro_text || 'This certificate is proudly presented to');
  const bodyText = replaceTokens(
    settings?.body_text ||
      'Empowering Women Human Rights Defenders Network recognizes the unwavering courage and care shown by {{participant_name}}.'
  );
  const quoteText = replaceTokens(settings?.quote_text || '');
  const showQr = settings?.qr_enabled ?? true;
  const fontStack = `${settings?.font_family || 'Playfair Display'}, 'Times New Roman', serif`;
  const qrUrl =
    showQr && (registration?.certificate || registration)
      ? `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(
          JSON.stringify({
            participant: participantName,
            training: trainingTitle,
            dates: dateSummary || '',
            issued: issuedDate,
            certificate_no: certificateNumber,
            verification_code: verificationCode,
          })
        )}`
      : null;

  return (
    <CertificatePreviewFrame>
      <CertificateCanvas style={{ aspectRatio }}>
        <CertificateFrameBorder>
          <CertificateContent style={{ fontFamily: fontStack }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              {logoSrc ? (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <img src={logoSrc} alt="Logo" style={{ width: 68, height: 'auto' }} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 42 }}>
                  <span style={{ fontWeight: 700, letterSpacing: '0.32em', fontSize: '11px' }}>EWHRDN</span>
                  <span style={{ fontWeight: 700, letterSpacing: '0.32em', fontSize: '11px' }}>EWHRDN</span>
                </div>
              )}
              <div>
                <h1>{titleText}</h1>
                <div style={{ width: 140, height: 2, background: '#cba74a', margin: '10px auto 5px' }} />
                <div style={{ width: 110, height: 2, background: '#cba74a', margin: '6px auto 14px' }} />
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14 }}>
              <div>
                <p style={{ letterSpacing: '0.26em', textTransform: 'uppercase', fontSize: 11, color: '#7a7d85' }}>
                  {introText}
                </p>
                <h2>{participantName}</h2>
                <div style={{ width: '52%', height: 1, background: '#d6ba72', margin: '10px auto 16px' }} />
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.5 }}>{bodyText}</p>
              {quoteText && (
                <p style={{ fontStyle: 'italic', color: '#5f6470', fontSize: 12, marginTop: 6 }}>
                  {quoteText}
                </p>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', textAlign: 'left', gap: 18, marginTop: 12 }}>
              <div style={{ flex: 1, fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}>
                <div>Issued on {issuedDate}</div>
                {location ? <div>{location}</div> : null}
                <div>Certificate No: {certificateNumber}</div>
                <div>Verification Code: {verificationCode}</div>
              </div>
              {signatureSrc || settings?.director_name || settings?.director_title ? (
                <div style={{ textAlign: 'left', fontSize: 11, color: '#4d5160' }}>
                  {signatureSrc ? (
                    <img
                      src={signatureSrc}
                      alt="Director signature"
                      style={{ width: 90, height: 45, objectFit: 'contain', marginBottom: 4 }}
                    />
                  ) : (
                    <div style={{ width: 120, height: 1, background: '#cba74a', marginBottom: 4 }} />
                  )}
                  {settings?.director_name && <div>{settings.director_name}</div>}
                  {settings?.director_title && (
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{settings.director_title}</div>
                  )}
                </div>
              ) : null}
              {showQr && qrUrl && (
                <div style={{ textAlign: 'center', fontSize: 11, color: '#4b5563' }}>
                  <img
                    src={qrUrl}
                    alt="QR preview"
                    style={{ width: 100, height: 100, border: '4px solid #f9f6ed', marginBottom: 4 }}
                  />
                  <div>Scan to confirm certificate details</div>
                </div>
              )}
            </div>
          </CertificateContent>
        </CertificateFrameBorder>
      </CertificateCanvas>
    </CertificatePreviewFrame>
  );
};

const buildTrainingDateSummary = (training) => {
  if (!training?.start_date) return '';
  const options = { month: 'long', day: '2-digit', year: 'numeric' };
  const start = new Date(training.start_date).toLocaleDateString(undefined, options);
  const endDate = training.end_date ? new Date(training.end_date).toLocaleDateString(undefined, options) : start;
  return start === endDate ? start : `${start} – ${endDate}`;
};

